import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceDot,
} from 'recharts';

import { formatJalaliDate } from '@/lib/format';
import { formatDecimal, formatNumber } from '@/features/reports/lib/format';
import { fa } from '../i18n/fa';
import ChartCard from './ChartCard';
import { ChartLegend, COLORS, rtlXAxisProps, rtlYAxisProps, chartTooltipStyle } from './chartTheme';

function TrendTooltip({ active, payload, units }) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;

  const priceUnit = fa.unitLabel(units?.price_unit);
  const perKg = `${priceUnit} / ${fa.unitLabel(units?.quantity_unit)}`;
  const difference = row.retail - row.factory;
  const sign = difference > 0 ? '+' : difference < 0 ? '−' : '';
  const differenceText = `${sign}${formatNumber(Math.abs(difference))} ${priceUnit}`;
  const percentText = `${sign}${formatDecimal(Math.abs((difference / row.factory) * 100), 1)}٪`;

  return (
    <div className="tr-trend-tooltip" style={chartTooltipStyle}>
      <p>{formatJalaliDate(row.date)}</p>
      <div>
        <span>{fa.charts.trend.factory}</span>
        <strong>
          {row.factory == null
            ? fa.charts.trend.noFactorySales
            : `${formatNumber(row.factory)} ${perKg}`}
        </strong>
      </div>
      <div>
        <span>{fa.charts.trend.retail}</span>
        <strong>{`${formatNumber(row.retail)} ${perKg}`}</strong>
      </div>
      {row.factory != null && row.factory > 0 && (
        <div>
          <span>{fa.charts.trend.priceGap}</span>
          <strong>{`${differenceText} (${percentText})`}</strong>
        </div>
      )}
    </div>
  );
}

export default function RetailTrendChart({ trend, units }) {
  const perKg = `${fa.unitLabel(units?.price_unit)} / ${fa.unitLabel(units?.quantity_unit)}`;
  const rows = trend.labels.map((label, i) => ({
    date: label,
    factory: trend.factory[i],
    retail: trend.retail[i],
  }));
  const hasFactory = rows.some((row) => row.factory != null);
  const prices = rows.flatMap((row) => [row.factory, row.retail]).filter(Number.isFinite);
  const min = prices.length ? Math.min(...prices) : 0;
  const max = prices.length ? Math.max(...prices) : 0;
  const range = Math.max(max - min, max * 0.04, 1);
  const priceDomain = [Math.max(0, min - range * 0.12), max + range * 0.12];
  const markerY = priceDomain[0] + range * 0.04;

  return (
    <ChartCard
      title={fa.charts.trend.title}
      note={fa.charts.trend.gapMarkerNote}
      empty={rows.length === 0 || !hasFactory}
      emptyMessage={rows.length > 0 && !hasFactory
        ? fa.charts.trend.noFactoryHistory
        : undefined}
    >
      <ResponsiveContainer>
        <LineChart data={rows} margin={{ top: 8, right: 8, left: 16, bottom: 8 }}>
          <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="date"
            {...rtlXAxisProps}
            minTickGap={36}
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => formatJalaliDate(value).slice(0, 7)}
          />
          <YAxis
            {...rtlYAxisProps}
            domain={priceDomain}
            tickFormatter={formatNumber}
            width={76}
            label={{
              value: perKg,
              angle: -90,
              position: 'insideRight',
              fill: COLORS.muted,
            }}
          />
          <Tooltip content={<TrendTooltip units={units} />} />
          <ChartLegend />
          {rows.filter((row) => row.factory == null).map((row) => (
            <ReferenceDot
              key={`missing-${row.date}`}
              x={row.date}
              y={markerY}
              r={7}
              fill="white"
              stroke={COLORS.amber}
              strokeWidth={2}
              shape={({ cx, cy }) => (
                <g>
                  <circle cx={cx} cy={cy} r={7} fill="white" stroke={COLORS.amber} strokeWidth={2} />
                  <path
                    d={`M${cx - 3},${cy - 3} L${cx + 3},${cy + 3} M${cx + 3},${cy - 3} L${cx - 3},${cy + 3}`}
                    stroke={COLORS.amber}
                    strokeWidth={2}
                  />
                </g>
              )}
            />
          ))}
          <Line
            dataKey="factory"
            name={fa.charts.trend.factory}
            stroke={COLORS.amber}
            dot={false}
            connectNulls={false}
          />
          <Line dataKey="retail" name={fa.charts.trend.retail} stroke={COLORS.primary} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
